import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { mappingApi } from '../../services/api';
import { farmerApi } from '../../services/api';

const CompanyDashboard = () => {
  const { user } = useAuth();
  const { companies } = useData();
  const [company, setCompany] = useState(null);
  const [mappedFarmers, setMappedFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalFarmers: 0,
    activeFarmers: 0,
    totalEmissions: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const companyId = user?.companyId;

        if (!companyId) {
          console.error('Company ID not found in user object:', user);
          throw new Error('Company ID not found. Please try logging out and logging in again.');
        }

        console.log('Fetching data for company ID:', companyId);

        // Fetch company profile
        const companyProfile = companies.find(c => c.id === companyId);
        if (companyProfile) {
          console.log('Found company profile:', companyProfile);
          setCompany(companyProfile);
        } else {
          console.warn('Company profile not found in context');
        }

        // Fetch mapped farmers
        console.log('Fetching mapped farmers...');
        try {
          const response = await mappingApi.getCompanyMappings(companyId);
          console.log('Raw mapped farmers API response:', response);

          let mappingsData = [];
          
          if (Array.isArray(response)) {
            console.log('Response is an array with', response.length, 'mappings');
            mappingsData = response;
          } else if (response && response.results && Array.isArray(response.results)) {
            console.log('Response has results array with', response.results.length, 'mappings');
            mappingsData = response.results;
          } else if (response && typeof response === 'object') {
            console.log('Response is an object, searching for array data');
            // Try to find an array in the response
            for (const key in response) {
              if (Array.isArray(response[key])) {
                console.log(`Found array in response at key: ${key} with ${response[key].length} items`);
                mappingsData = response[key];
                break;
              }
            }
          }

          console.log('Processed mappings data:', mappingsData);

          if (mappingsData.length === 0) {
            console.log('No mappings found for company');
            setMappedFarmers([]);
            setStats({
              totalFarmers: 0,
              activeFarmers: 0,
              totalEmissions: 0
            });
            return;
          }

          // Get farmer details for each mapping
          console.log('Fetching farmer details for', mappingsData.length, 'mappings');
          const farmersWithDetails = await Promise.all(
            mappingsData.map(async (mapping) => {
              try {
                const farmerId = typeof mapping.farmer === 'object' ? mapping.farmer.id : mapping.farmer;
                console.log('Fetching details for farmer ID:', farmerId);
                const farmerResponse = await farmerApi.getFarmer(farmerId);
                console.log('Farmer details response:', farmerResponse);
                return {
                  ...mapping,
                  name: farmerResponse.name || farmerResponse.farmer_name || 'Unknown Farmer',
                  village: farmerResponse.village || '',
                  district: farmerResponse.district || '',
                  location: farmerResponse.location || '',
                  phone: farmerResponse.phone || '',
                  email: farmerResponse.email || '',
                  status: mapping.status || 'active',
                  mappedDate: mapping.created_at || mapping.mappedDate
                };
              } catch (error) {
                console.error(`Error fetching farmer details for ID ${mapping.farmer}:`, error);
                return {
                  ...mapping,
                  name: 'Unknown Farmer',
                  village: '',
                  district: '',
                  location: '',
                  phone: '',
                  email: '',
                  status: mapping.status || 'active',
                  mappedDate: mapping.created_at || mapping.mappedDate
                };
              }
            })
          );

          console.log('Farmers with details:', farmersWithDetails);
          setMappedFarmers(farmersWithDetails);

          // Calculate stats
          const activeFarmers = farmersWithDetails.filter(farmer => farmer.status === 'active').length;
          setStats({
            totalFarmers: farmersWithDetails.length,
            activeFarmers,
            totalEmissions: 0 // This will be updated when emissions data is fetched
          });
        } catch (mappingError) {
          console.error('Error fetching mapped farmers:', mappingError);
          console.error('Error details:', {
            message: mappingError.message,
            response: mappingError.response,
            status: mappingError.response?.status
          });
          setError('Failed to fetch mapped farmers. Please try again.');
          setMappedFarmers([]);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          status: error.response?.status
        });
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, companies]);

  if (loading) {
    return <div style={styles.loading}>Loading dashboard...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!company) {
    return <div style={styles.error}>Company profile not found.</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Company Dashboard</h1>
        <div style={styles.companyInfo}>
          <h2 style={styles.companyName}>{company.name}</h2>
          <p style={styles.companyEmail}>{company.email}</p>
        </div>
      </div>

      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Farmers</h3>
          <p style={styles.statValue}>{stats.totalFarmers}</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Active Farmers</h3>
          <p style={styles.statValue}>{stats.activeFarmers}</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Emissions</h3>
          <p style={styles.statValue}>{stats.totalEmissions} kg CO2e</p>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Mapped Farmers</h2>
          <div style={styles.sectionStats}>
            <span style={styles.statBadge}>Total: {mappedFarmers.length}</span>
            <span style={styles.statBadge}>Active: {stats.activeFarmers}</span>
          </div>
        </div>

        {mappedFarmers.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No farmers mapped yet.</p>
          </div>
        ) : (
          <div style={styles.farmersList}>
            {mappedFarmers.map((farmer) => (
              <div key={farmer.id} style={styles.farmerCard}>
                <div style={styles.farmerInfo}>
                  <div style={styles.farmerHeader}>
                    <div style={styles.farmerName}>
                      {farmer.name || farmer.farmer_name || 'Unknown Farmer'}
                    </div>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: farmer.status === 'active' ? 'var(--success-color)' : 'var(--danger-color)'
                    }}>
                      {farmer.status || 'active'}
                    </span>
                  </div>
                  <div style={styles.farmerDetails}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Phone:</span>
                      <span style={styles.detailValue}>{farmer.phone || farmer.mobile || farmer.contact_number || 'Not set'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Email:</span>
                      <span style={styles.detailValue}>{farmer.email || farmer.contact_email || farmer.email_address || 'Not set'}</span>
                    </div>
                     <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Village:</span>
                      <span style={styles.detailValue}>{farmer.village || 'Not set'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>District:</span>
                      <span style={styles.detailValue}>{farmer.district || 'Not set'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Location:</span>
                      <span style={styles.detailValue}>{farmer.location || farmer.village || farmer.district || 'Not set'}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Mapped Date:</span>
                      <span style={styles.detailValue}>
                        {farmer.mappedDate ? new Date(farmer.mappedDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </div>
                    {farmer.created_at && (
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>Created:</span>
                        <span style={styles.detailValue}>
                          {new Date(farmer.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {farmer.updated_at && (
                       <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>Last Updated:</span>
                        <span style={styles.detailValue}>
                          {new Date(farmer.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={styles.farmerActions}>
                  <button
                    onClick={() => window.location.href = `/company/farmer/${farmer.id}`}
                    style={styles.viewButton}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '24px',
    marginBottom: '10px'
  },
  companyInfo: {
    marginBottom: '20px'
  },
  companyName: {
    fontSize: '20px',
    marginBottom: '5px'
  },
  companyEmail: {
    color: '#666'
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  statTitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '10px'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold'
  },
  section: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '20px',
    marginBottom: '20px'
  },
  farmersList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  farmerCard: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  farmerInfo: {
    flex: 1,
    marginRight: '20px'
  },
  farmerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #eee'
  },
  farmerName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--text-color)'
  },
  farmerDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '10px'
  },
  detailRow: {
    display: 'flex',
    marginBottom: '8px',
    padding: '5px 0'
  },
  detailLabel: {
    fontWeight: 'bold',
    width: '120px',
    color: '#666',
    fontSize: '14px'
  },
  detailValue: {
    flex: 1,
    fontSize: '14px',
    color: 'var(--text-color)'
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--danger-color)',
    fontSize: '18px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionStats: {
    display: 'flex',
    gap: '10px'
  },
  statBadge: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '15px',
    fontSize: '14px'
  },
  farmerActions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end',
    minWidth: '120px'
  },
  viewButton: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: 'var(--primary-dark)'
    }
  }
};

export default CompanyDashboard; 